import { Alert, Stack } from '@mui/material';
import { t } from 'i18next';
import NetworkAnalyzerIcon from '../../../assets/images/tools/network-analyzer-color-icon.svg';
import JavaScannerIcon from '../../../assets/images/tools/java-scanner-color-icon.svg';
import JavaTracerIcon from '../../../assets/images/tools/java-tracer-color-icon.svg';
import LinuxScannerIcon from '../../../assets/images/tools/linux-color-icon.svg';
import WindowsScannerIcon from '../../../assets/images/tools/windows-color-icon.svg';
import { GenericError } from '../../../common/components/GenericError';
import { InlineLoader } from '../../../common/components/InlineLoader';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { AuthorizationError } from '../../../common/errors/errors';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { bind } from '../../../common/utils/decorators';
import { Interpolation } from '../../../config/i18n/components/Interpolation';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { EXTERNAL_URLS, PATHS, TabNameLocalizationKeys } from '../../../routing/routes';
import { SensorsGroupCard, SensorGroup } from '../components/SensorsGroupCard';
import { OrganizationSensorsRepository } from '../services/OrganizationSensorsRepository';

type State = BaseState & {
  loading: boolean;
};

type Props = {};

export class OrganizationSensorsController extends BaseController<Props, State> {
  private readonly repository = new OrganizationSensorsRepository();

  private groups: SensorGroup[] = [];

  protected getBaseState(): State {
    return { ...super.getBaseState(), loading: true };
  }

  componentWillUnmount(): void {
    super.componentWillUnmount();

    this.repository.cancelAllRequests();
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const permission = await this.permissionsService.canDownloadTracers();
    if (!permission) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }
    this.load();
  }

  render() {
    const { loading } = this.state;

    const hasData = this.groups.length > 0;
    const hasError = !loading && this.groups.length === 0;

    return this.renderBase(
      <SecondaryNav title={t(TabNameLocalizationKeys.SENSORS)} getBreadcrumbs={this.getBreadcrumbs}>
        <Stack direction="column" gap={2}>
          {loading && <InlineLoader />}
          {hasError && <GenericError onClick={this.load} />}
          {hasData && (
            <Alert severity="info">
              <Interpolation
                i18nKey="sensorsList.docs"
                variant="body2"
                items={[{ name: 'docs', href: UrlHelper.wrapExternalDocsUrl(EXTERNAL_URLS.DOCS_SENSORS) }]}
              />
            </Alert>
          )}
          {hasData && this.groups.map((g, gIndex) => <SensorsGroupCard key={gIndex} {...g} />)}
        </Stack>
      </SecondaryNav>
    );
  }

  @bind
  private async load() {
    try {
      this.setState({ loading: true });

      const {
        ANALYSIS: { JAVA_SCANNER, NETWORK_ANALYZER },
        APP: { JAVA: JAVA_TRACER },
        FILESYSTEM: { LINUX: LINUX_SCANNER, WINDOWS: WINDOWS_SCANNER },
      } = await this.repository.getSensors();

      this.groups = [
        {
          name: t('sensorsList.application.title'),
          description: t('sensorsList.application.description'),
          sensors: [
            {
              name: t('sensorsList.application.java.name'),
              description: t('sensorsList.application.java.description'),
              icon: <JavaTracerIcon />,
              url: JAVA_TRACER?.url,
              version: JAVA_TRACER?.version,
              docsUrl: EXTERNAL_URLS.DOCS_JAVA_TRACER,
              docsAriaLabel: t('a11y.sensors.application.java.docs'),
              downloadAriaLabel: t('a11y.sensors.application.java.download', { version: JAVA_TRACER?.version ?? '' }),
            },
            {
              name: t('sensorsList.application.javaScanner.name'),
              description: t('sensorsList.application.javaScanner.description'),
              icon: <JavaScannerIcon />,
              url: JAVA_SCANNER?.url,
              version: JAVA_SCANNER?.version,
              docsUrl: EXTERNAL_URLS.DOCS_JAVA_SCANNER,
              docsAriaLabel: t('a11y.sensors.application.javaScanner.docs'),
              downloadAriaLabel: t('a11y.sensors.application.javaScanner.download', { version: JAVA_SCANNER?.version ?? '' }),
            },
          ],
        },
        {
          name: t('sensorsList.host.title'),
          description: t('sensorsList.host.description'),
          sensors: [
            {
              name: t('sensorsList.host.linux.name'),
              description: t('sensorsList.host.linux.description'),
              icon: <LinuxScannerIcon />,
              url: LINUX_SCANNER?.url,
              version: LINUX_SCANNER?.version,
              docsUrl: EXTERNAL_URLS.DOCS_HOST_SCANNER_LINUX,
              docsAriaLabel: t('a11y.sensors.host.linux.docs'),
              downloadAriaLabel: t('a11y.sensors.host.linux.download', { version: LINUX_SCANNER?.version ?? '' }),
            },
            {
              name: t('sensorsList.host.windows.name'),
              description: t('sensorsList.host.windows.description'),
              icon: <WindowsScannerIcon />,
              url: WINDOWS_SCANNER?.url,
              version: WINDOWS_SCANNER?.version,
              docsUrl: EXTERNAL_URLS.DOCS_HOST_SCANNER_WINDOWS,
              docsAriaLabel: t('a11y.sensors.host.windows.docs'),
              downloadAriaLabel: t('a11y.sensors.host.windows.download', { version: WINDOWS_SCANNER?.version ?? '' }),
            },
          ],
        },
        {
          name: t('sensorsList.network.title'),
          description: t('sensorsList.network.description'),
          sensors: [
            {
              name: t('sensorsList.network.networkAnalyzer.name'),
              description: t('sensorsList.network.networkAnalyzer.description'),
              icon: <NetworkAnalyzerIcon />,
              url: NETWORK_ANALYZER?.url,
              version: NETWORK_ANALYZER?.version,
              docsUrl: EXTERNAL_URLS.DOCS_NETWORK_ANALYZER,
              docsAriaLabel: t('a11y.sensors.network.networkAnalyzer.docs'),
              downloadAriaLabel: t('a11y.sensors.network.networkAnalyzer.download', { version: NETWORK_ANALYZER?.version ?? '' }),
            },
          ],
        },
      ];

      this.setState({ loading: false });
    } catch (e: unknown) {
      this.setState({ loading: false });
      this.handleError(e);
    }
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getSensorsBreadcrumbs();
  }
}
